import React, { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import Logo, { LogoProps } from './Logo';
// import { logger } from "../../utils/logger";
export interface ResponsiveLogoProps extends Omit<LogoProps, 'size'> {
  mobileVariant?: 'icon' | 'full';
  tabletVariant?: 'icon' | 'full';
  desktopVariant?: 'icon' | 'full';
  mobileSize?: 'small' | 'medium' | 'mobile';
  tabletSize?: 'small' | 'medium' | 'large';
  desktopSize?: 'medium' | 'large' | 'xlarge';
  wide?: boolean;
}

const ResponsiveLogo: React.FC<ResponsiveLogoProps> = ({
  mobileVariant = 'icon',
  tabletVariant = 'full',
  desktopVariant = 'full',
  mobileSize = 'small',
  tabletSize = 'medium',
  desktopSize = 'medium',
  variant = 'full',
  transparent = true,
  wide = false,
  ...props
}) => {
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode;
  
  // Match Header: mobile drawer vs desktop nav at `md` (900px), not `sm` (600px).
  // defaultMatches from current viewport avoids icon↔full logo swap after first paint (CLS).
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'), {
    defaultMatches: typeof window !== 'undefined' ? window.innerWidth < muiTheme.breakpoints.values.md : false,
    noSsr: true,
  });
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'), {
    defaultMatches: typeof window !== 'undefined' ? window.innerWidth >= muiTheme.breakpoints.values.md : true,
    noSsr: true,
  });

  // Memoize responsive calculations to prevent unnecessary re-renders
  const { responsiveVariant, responsiveSize } = useMemo(() => {
    let finalVariant = variant;
    let finalSize: 'small' | 'medium' | 'large' | 'xlarge' | 'mobile' = 'medium';

    if (isMobile) {
      finalVariant = mobileVariant;
      finalSize = mobileSize as 'small' | 'medium' | 'mobile';
    } else if (isDesktop) {
      // Between sm and md Header used to show "tablet" logo while nav was still mobile; align with md+ desktop nav.
      finalVariant = desktopVariant;
      finalSize = desktopSize as 'medium' | 'large' | 'xlarge';
    }

    return { responsiveVariant: finalVariant, responsiveSize: finalSize };
  }, [isMobile, isDesktop, mobileVariant, desktopVariant, mobileSize, desktopSize, variant]);



  return (
    <Logo
      variant={responsiveVariant}
      size={responsiveSize}
      transparent={transparent}
      wide={wide}
      {...props}
    />
  );
};

export default ResponsiveLogo; 
